import { Button, Form, Input, Popconfirm, Table, Typography } from 'antd';
import UserSearch from 'components/UserSearch';
import LocaleContext from 'locales';
import moment from 'moment';
import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getApprovalLinesByType } from 'services/websocket/approval';
import { v4 as uuidv4 } from 'uuid';

/**
 * 컴포넌트: 결재선 설정 및 보기
 * @param {boolean} isCreate 생성인지 여부
 * @param {*} form Form.ref
 * @returns ReactNode
 */
export default function FormItemApprovalLine(props) {
  const { isCreate, form, backConf, type } = props;

  // hooks
  const { locale } = useContext(LocaleContext);

  // 결재 유형 타입 정의
  const approvalTypes = useMemo(
    () => ({
      0: locale.resource['txt.label.approval'],
    }),
    [locale.resource],
  );

  // states
  const [userSearchNamePath, setUserSearchNamePath] = useState(undefined);
  const [approvalLineByType, setApprovalLineByType] = useState([]);

  // 결재선 옵션이 사용자 결재인지 여부
  const isUserApproval = useMemo(
    () => backConf && backConf.approval.approvalLineOption.type === 0,
    [backConf],
  );

  // 유틸리티 함수: getApprovalLinesByType 데이터인지 확인 (idx만 체크)
  const isFromApprovalType = useCallback(
    (record) => {
      return approvalLineByType.some((typeItem) => typeItem.idx === record.idx);
    },
    [approvalLineByType],
  );

  // 유틸리티 함수: 사용자 추가 데이터만 필터링
  const filterUserAddedData = useCallback(
    (data) => {
      return data.filter((item) => !isFromApprovalType(item));
    },
    [isFromApprovalType],
  );

  // 유틸리티 함수: level 재계산
  const recalculateLevels = useCallback((approvalLines) => {
    return approvalLines.map((item, index) => ({
      ...item,
      level: index + 1,
    }));
  }, []);

  // 타입별 기본 결재선 데이터 로드 및 설정
  useEffect(() => {
    try {
      if (isCreate && type) {
        // 서비스 타입별 기본 결재선 조회
        getApprovalLinesByType(type).then((res) => {
          if (res?.[0]?.approvers?.length > 0) {
            // getApprovalLinesByType 데이터를 가공하여 approvalLines 형식에 맞게 변환
            const processedApprovalLines = res[0].approvers.map((item, index) => ({
              idx: item.idx || uuidv4(), // idx가 있으면 그대로 사용, 없으면 uuid 생성
              level: index + 1, // level은 index + 1로 설정
              approver: item, // approver에는 배열의 항목을 그대로 넣음
            }));

            // 결재선 초기값 설정 (disabled 처리를 위한 참조용)
            setApprovalLineByType(processedApprovalLines);

            form.setFieldsValue({
              approvalLines: processedApprovalLines,
            });
          }
        });
      }
    } catch (error) {
      console.error(error);
    }
  }, [type, form]);

  // 결재선 추가 시 (사용자가 수동으로 결재자 추가)
  const onAddApprovalLine = useCallback(() => {
    const prevData = form.getFieldValue('approvalLines') || [];

    // getApprovalLinesByType 데이터를 제외한 사용자 추가 데이터만 가져오기
    const userAddedData = filterUserAddedData(prevData);

    // 새로운 결재선 항목 추가 (사용자가 수동으로 추가하는 항목)
    const newApprovalLine = {
      idx: uuidv4(),
      level: userAddedData.length + 1,
      approver: null,
    };

    // 사용자 추가 데이터 + 새로운 항목 + getApprovalLinesByType 데이터 순서로 설정
    const finalApprovalLines = [...userAddedData, newApprovalLine, ...approvalLineByType];

    // level을 전체 순서에 맞게 재계산
    const updatedApprovalLines = recalculateLevels(finalApprovalLines);

    form.setFieldsValue({
      approvalLines: updatedApprovalLines,
    });
  }, [form, filterUserAddedData, approvalLineByType, recalculateLevels]);

  // 결재선 삭제 시 (사용자가 수동으로 추가한 결재선만 삭제 가능)
  const onRemoveApprovalLine = useCallback(
    (record) => {
      const prevData = form.getFieldValue('approvalLines') || [];

      // 삭제할 항목 제거
      const filteredData = prevData.filter((el) => el.idx !== record.idx);

      // getApprovalLinesByType 데이터를 제외한 사용자 추가 데이터만 가져오기
      const userAddedData = filterUserAddedData(filteredData);

      // 사용자 추가 데이터 + getApprovalLinesByType 데이터 순서로 설정하고 level 재계산
      const finalApprovalLines = [...userAddedData, ...approvalLineByType];
      const updatedApprovalLines = recalculateLevels(finalApprovalLines);

      form.setFieldsValue({ approvalLines: updatedApprovalLines });
    },
    [form, filterUserAddedData, approvalLineByType, recalculateLevels],
  );

  // 결재선 벨리데이터 (필수 입력, 결재자 선택, 중복 체크)
  const approvalLinesValidators = useMemo(
    () => [
      () => ({
        validator(_, value) {
          if (!value.length) {
            return Promise.reject(new Error(locale.resource['txt.required-field']));
          }
          return Promise.resolve();
        },
      }),
      () => ({
        validator(_, value) {
          const result = value.find((el) => !el.approver);
          if (result) {
            return Promise.reject(new Error(locale.resource['txt-please-select']));
          }
          return Promise.resolve();
        },
      }),
      () => ({
        // 중복 결재자 체크 (동일한 사용자가 여러 번 결재선에 포함되지 않도록)
        validator(_, value) {
          const sAMAccountNames = value.map((el) => el.approver?.sAMAccountName);
          if (new Set(sAMAccountNames).size !== sAMAccountNames.length) {
            return Promise.reject(new Error(locale.resource['txt-duplicate-approver']));
          }
          return Promise.resolve();
        },
      }),
    ],
    [locale.resource],
  );

  // Table columns 메모이제이션
  const tableColumns = useMemo(
    () => [
      {
        title: locale.resource['txt.level'],
        dataIndex: 'level',
        width: 48,
        align: 'right',
      },
      {
        title: locale.resource['txt.kind'],
        dataIndex: 'type',
        render: (text) => approvalTypes[text],
      },
      {
        title: `${locale.resource['txt.approver']}(${locale.resource['txt.id']})`,
        dataIndex: 'approver',
        width: 200,
        render: (data, record, index) => {
          const text = data;
          if (text?.deletedUser) {
            text.userName = locale.resource['txt.deleted-user'];
          }

          const isFromType = isFromApprovalType(record);

          return !isFromType ? (
            <Input.Search
              readOnly={!isCreate || isFromType}
              disabled={!isCreate}
              value={text && `${text.userName ? text.userName : '-'}(${text.sAMAccountName})`}
              onSearch={() => !isCreate || isFromType || setUserSearchNamePath([index, 'approver'])}
              color={text?.deletedUser ? 'danger' : 'default'}
              enterButton={false}
            />
          ) : (
            <Typography.Text type={text?.deletedUser ? 'danger' : 'default'}>
              {`${text?.deletedUser ? locale.resource['txt.deleted-user'] : text?.userName}(${
                text?.sAMAccountName
              })`}
            </Typography.Text>
          );
        },
      },
      {
        title: locale.resource['txt.status'],
        dataIndex: 'status',
        render: (text) => locale.resource[text],
      },
      {
        title: locale.resource['txt.approvalDate'],
        width: 140,
        dataIndex: 'approvalDate',
        render: (text) => (text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : ''),
      },
      {
        title: locale.resource['txt.action'],
        width: 60,
        align: 'right',
        render: (_, record) =>
          isCreate ? (
            <Popconfirm
              title={locale.resource['txt.ask.delete']}
              onConfirm={() => onRemoveApprovalLine(record)}
              disabled={record.disabled || isFromApprovalType(record)}
              okText={locale.resource['txt.delete']}
              okButtonProps={{ danger: true }}
            >
              <Button
                type="link"
                style={{ padding: 0 }}
                disabled={record.disabled || isFromApprovalType(record)}
              >
                {locale.resource['txt.delete']}
              </Button>
            </Popconfirm>
          ) : null,
      },
    ],
    [locale.resource, approvalTypes, isCreate, isFromApprovalType, onRemoveApprovalLine],
  );

  // UserSearch 선택 핸들러 메모이제이션
  const handleUserSelected = useCallback(
    (selectedUser) => {
      if (!selectedUser) {
        setUserSearchNamePath(undefined);
        return;
      }
      const [index, property] = userSearchNamePath;
      const currentApprovalLines = form.getFieldValue('approvalLines') || [];

      // 새로운 배열을 생성하여 불변성 유지
      const newApprovalLines = [...currentApprovalLines];
      newApprovalLines[index] = {
        ...newApprovalLines[index],
        [property]: selectedUser,
      };

      form.setFieldsValue({
        approvalLines: newApprovalLines,
      });
      setUserSearchNamePath(undefined);
    },
    [userSearchNamePath, form],
  );

  // 관리자 결재인 경우 출력하지 않는다.
  if (!isUserApproval) {
    return null;
  }

  return (
    <>
      <Form.Item
        label={locale.resource['txt.label.approval']}
        name="approvalLines"
        valuePropName="dataSource"
        initialValue={[]}
        required
        rules={approvalLinesValidators}
      >
        <Table
          size="small"
          pagination={false}
          rowKey="idx"
          footer={
            isCreate
              ? () => (
                  <Button type="dashed" block onClick={onAddApprovalLine}>
                    {locale.resource['txt.create']}
                  </Button>
                )
              : null
          }
          columns={tableColumns}
        />
      </Form.Item>
      <UserSearch visible={userSearchNamePath} onSelected={handleUserSelected} />
    </>
  );
}
